import { Injectable } from '@angular/core';

export interface SessionDeviceHeaders {
    DeviceId: string;
    DeviceType: string;
    OperatingSystem: string;
    ClientName: string;
    ClientVersion: string;
    DeviceOsVersion: string;
    DeviceModel: string;
}

interface UserAgentBrandVersion {
    brand: string;
    version: string;
}

interface UserAgentData {
    brands?: UserAgentBrandVersion[];
    mobile?: boolean;
    platform?: string;
}

interface NavigatorWithUserAgentData extends Navigator {
    userAgentData?: UserAgentData;
}

interface BrowserInfo {
    name: string;
    version: string;
}

@Injectable({
    providedIn: 'root',
})
export class SessionDeviceInfoService {
    private readonly deviceIdStorageKey = 'datacheck.angular.gov.auth-device-id.v1';
    private readonly unknownValue = 'Unknown';

    public async getLoginHeaders(): Promise<SessionDeviceHeaders> {
        const userAgent = this.getUserAgent();
        const userAgentData = this.getUserAgentData();
        const browserInfo = this.getBrowserInfo(userAgentData, userAgent);
        const deviceType = this.getDeviceType(userAgentData, userAgent);

        return {
            DeviceId: this.getDeviceId(),
            DeviceType: deviceType,
            OperatingSystem: this.getOperatingSystem(userAgentData?.platform, userAgent),
            ClientName: browserInfo.name,
            ClientVersion: browserInfo.version,
            DeviceOsVersion: this.getDeviceOsVersion(userAgent),
            DeviceModel: this.getDeviceModel(deviceType),
        };
    }

    private getDeviceId(): string {
        const storedDeviceId = this.getStoredDeviceId();

        if (storedDeviceId) {
            return storedDeviceId;
        }

        const deviceId = this.createDeviceId();

        this.storeDeviceId(deviceId);

        return deviceId;
    }

    private getStoredDeviceId(): string | null {
        if (typeof localStorage === 'undefined') {
            return null;
        }

        try {
            return localStorage.getItem(this.deviceIdStorageKey);
        } catch {
            return null;
        }
    }

    private storeDeviceId(deviceId: string): void {
        if (typeof localStorage === 'undefined') {
            return;
        }

        try {
            localStorage.setItem(this.deviceIdStorageKey, deviceId);
        } catch {
            return;
        }
    }

    private createDeviceId(): string {
        if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
            return window.crypto.randomUUID();
        }

        return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }

    private getUserAgent(): string {
        if (typeof navigator === 'undefined') {
            return '';
        }

        return navigator.userAgent || '';
    }

    private getUserAgentData(): UserAgentData | undefined {
        if (typeof navigator === 'undefined') {
            return undefined;
        }

        return (navigator as NavigatorWithUserAgentData).userAgentData;
    }

    private getOperatingSystem(platform = '', userAgent: string): string {
        const source = `${platform} ${userAgent}`.toLowerCase();

        if (source.includes('android')) {
            return 'Android';
        }

        if (/iphone|ipad|ipod/.test(source)) {
            return 'iOS';
        }

        if (/windows|win32|win64/.test(source)) {
            return 'Windows';
        }

        if (/mac os|macos|macintosh|macintel|macppc|mac68k/.test(source)) {
            return 'macOS';
        }

        if (/linux|x11/.test(source)) {
            return 'Linux';
        }

        return this.unknownValue;
    }

    private getBrowserInfo(userAgentData: UserAgentData | undefined, userAgent: string): BrowserInfo {
        return (
            this.getBrowserInfoFromBrands(userAgentData?.brands) ??
            this.getBrowserInfoFromUserAgent(userAgent) ?? {
                name: 'Other',
                version: this.unknownValue,
            }
        );
    }

    private getBrowserInfoFromBrands(
        brands: UserAgentBrandVersion[] | undefined,
    ): BrowserInfo | null {
        if (!brands?.length) {
            return null;
        }

        const knownBrands = [
            { match: 'Microsoft Edge', name: 'Edge' },
            { match: 'Google Chrome', name: 'Chrome' },
            { match: 'Firefox', name: 'Firefox' },
            { match: 'Safari', name: 'Safari' },
            { match: 'Chromium', name: 'Chrome' },
        ];

        for (const knownBrand of knownBrands) {
            const brand = brands.find(item => item.brand.includes(knownBrand.match));

            if (brand) {
                return {
                    name: knownBrand.name,
                    version: brand.version || this.unknownValue,
                };
            }
        }

        return null;
    }

    private getBrowserInfoFromUserAgent(userAgent: string): BrowserInfo | null {
        const edgeMatch = userAgent.match(/(?:Edg|EdgA|EdgiOS|Edge)\/([\d.]+)/);

        if (edgeMatch) {
            return { name: 'Edge', version: edgeMatch[1] };
        }

        const firefoxMatch = userAgent.match(/Firefox\/([\d.]+)/);

        if (firefoxMatch) {
            return { name: 'Firefox', version: firefoxMatch[1] };
        }

        const chromeMatch = userAgent.match(/(?:Chrome|CriOS)\/([\d.]+)/);

        if (chromeMatch) {
            return { name: 'Chrome', version: chromeMatch[1] };
        }

        const safariMatch = userAgent.match(/Version\/([\d.]+).*Safari\//);

        if (safariMatch) {
            return { name: 'Safari', version: safariMatch[1] };
        }

        return null;
    }

    private getDeviceType(userAgentData: UserAgentData | undefined, userAgent: string): string {
        if (!userAgent && !userAgentData) {
            return this.unknownValue;
        }

        if (this.isTablet(userAgent)) {
            return 'Tablet';
        }

        if (userAgentData?.mobile || this.isMobile(userAgent)) {
            return 'Phone';
        }

        return 'PC';
    }

    private getDeviceOsVersion(userAgent: string): string {
        const windowsMatch = userAgent.match(/Windows NT ([\d.]+)/);

        if (windowsMatch) {
            return windowsMatch[1];
        }

        const androidMatch = userAgent.match(/Android ([\d.]+)/);

        if (androidMatch) {
            return androidMatch[1];
        }

        const iosMatch = userAgent.match(/(?:iPhone|iPad|iPod).*OS ([\d_]+)/);

        if (iosMatch) {
            return iosMatch[1].replace(/_/g, '.');
        }

        const macMatch = userAgent.match(/Mac OS X ([\d_]+)/);

        if (macMatch) {
            return macMatch[1].replace(/_/g, '.');
        }

        return this.unknownValue;
    }

    private getDeviceModel(deviceType: string): string {
        if (deviceType === 'PC') {
            return 'Desktop';
        }

        if (deviceType === 'Phone') {
            return 'Mobile';
        }

        return deviceType;
    }

    private isTablet(userAgent: string): boolean {
        return /ipad|tablet|playbook|silk|android(?!.*mobile)/i.test(userAgent);
    }

    private isMobile(userAgent: string): boolean {
        return /mobile|iphone|ipod|android|windows phone/i.test(userAgent);
    }
}
